/**
 * Turning one box's edit into the whole-record write the safety message API
 * takes.
 *
 * `PUT /ics_safety_messages/:id` replaces the record rather than patching it,
 * so a card that saves per box has to resend everything it is not changing.
 * Reading that back out of the DTO in one place is what stops a box's save from
 * quietly blanking a field it never showed.
 */

import type {
  BaseSafetyMessage,
  SafetyMessageDto,
} from '../../../../../types/iapBaseTypes';

export type SafetyPlanPatch = Partial<BaseSafetyMessage>;

/** No message for the period yet, which every reader here treats as all blanks. */
type MaybeMessage = SafetyMessageDto | null | undefined;

/** Everything the record holds, as the API wants it back. */
export const toSafetyMessagePayload = (
  message: MaybeMessage,
  patch: SafetyPlanPatch
): BaseSafetyMessage => ({
  safetyOfficer: message?.safetyOfficer,
  dutyPhone: message?.dutyPhone,
  email: message?.email,
  datetimeInitiated: message?.datetimeInitiated,
  knownHazards: message?.knownHazards,
  safetyMessage: message?.safetyMessage,
  sopAndSafeWorkPractices: message?.sopAndSafeWorkPractices,
  requiredPpe: message?.requiredPpe,
  comments: message?.comments,
  healthAndSafetyInfo: message?.healthAndSafetyInfo,
  ...patch,
});

/** What box 5 holds, flattened out of the `known_hazards` jsonb. */
export type KnownHazardsDraft = {
  physicalHazards: string[];
  environmentalHazards: string[];
  otherHazards: string[];
  otherHazardsText: string;
};

export const toKnownHazardsDraft = (
  message: MaybeMessage
): KnownHazardsDraft => ({
  physicalHazards: message?.knownHazards?.physicalHazards ?? [],
  environmentalHazards: message?.knownHazards?.environmentalHazards ?? [],
  otherHazards: message?.knownHazards?.otherHazards ?? [],
  otherHazardsText: message?.knownHazards?.otherHazardsText ?? '',
});

/**
 * The jsonb shape goes back unchanged, with empty collections dropped rather
 * than stored as empty arrays, which is how the form wrote them before.
 */
export const fromKnownHazardsDraft = (draft: KnownHazardsDraft) => ({
  physicalHazards:
    draft.physicalHazards.length > 0 ? draft.physicalHazards : undefined,
  environmentalHazards:
    draft.environmentalHazards.length > 0
      ? draft.environmentalHazards
      : undefined,
  otherHazards: draft.otherHazards.length > 0 ? draft.otherHazards : undefined,
  otherHazardsText: draft.otherHazardsText || undefined,
});

/** What box 9 holds, flattened out of the `health_and_safety_info` jsonb. */
export type HealthAndSafetyDraft = { hasPlan: string; location: string };

export const toHealthAndSafetyDraft = (
  message: MaybeMessage
): HealthAndSafetyDraft => ({
  hasPlan: message?.healthAndSafetyInfo?.hasPlan ?? '',
  location: message?.healthAndSafetyInfo?.location ?? '',
});

/** An unanswered question with no location is stored as absent, not as blanks. */
export const fromHealthAndSafetyDraft = (draft: HealthAndSafetyDraft) =>
  draft.hasPlan || draft.location ? draft : undefined;

/**
 * The datetime-local input's value for a stored timestamp, and back.
 *
 * The input speaks local time with no zone, which is what the printed box says
 * too, so the conversion is deliberately naive on both sides.
 */
export const toDateTimeLocalValue = (value: Date | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const fromDateTimeLocalValue = (value: string): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};
